import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, Star, Utensils, Users, Pencil, EyeOff, MessageCircle } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LoadingState, RecipeEmptyState } from "../components/states";
import { FollowButton } from "../components/FollowButton";
import { CommentThread } from "../components/CommentThread";
import { ReviewList, RateBox } from "../components/ReviewList";
import { RatingStars } from "../components/RatingStars";
import { ShareButtons } from "../components/ShareButtons";

function RecipeTile({ r, chefSlug }) {
  return (
    <Link to={`/chefs/${chefSlug}/${r.slug}`} data-testid={`chef-recipe-${r.slug}`}
      className="group relative block aspect-[4/3] rounded-3xl overflow-hidden border border-border shadow-soft hover:shadow-lift hover:-translate-y-1 transition-all duration-300">
      <img src={r.thumbnail} alt={r.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-ink/80 to-transparent" />
      <div className="absolute left-4 right-4 bottom-4 text-white">
        <h3 className="font-heading text-lg font-black leading-tight line-clamp-2 drop-shadow">{r.title}</h3>
        <p className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-white/90"><Star size={12} className="fill-gold text-gold" /> {r.rating_avg} · {r.cook_time} min</p>
      </div>
    </Link>
  );
}

export default function ChefProfile() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [interactions, setInteractions] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setData(null);
    api.get(`/chefs/${slug}`).then(({ data }) => {
      setData(data); setRecipes(data.recipes); setPage(1); setHasMore(data.recipes.length < data.recipes_total); setReviews(data.reviews || []);
      api.get(`/chefs/${data.chef.id}/interactions`).then((r) => setInteractions(r.data.interactions)).catch(() => {});
      if (window.location.hash === "#community") setTimeout(() => document.getElementById("community")?.scrollIntoView({ behavior: "smooth" }), 300);
    }).catch(() => setData(false));
  }, [slug, user]);

  const loadMore = async () => {
    const next = page + 1;
    const { data: d } = await api.get(`/chefs/${slug}/recipes`, { params: { page: next, limit: 9 } });
    setRecipes((p) => [...p, ...d.recipes]); setHasMore(d.has_more); setPage(next);
  };

  if (data === null) return <LoadingState label="Loading chef…" />;
  if (!data) return <div className="py-16"><RecipeEmptyState title="Chef not found" subtitle="This profile may be private or removed." /></div>;

  const { chef } = data;
  const overall = chef.rating_avg || 0;

  return (
    <div className="pb-12" data-testid="chef-profile">
      {/* Header */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-8">
        {chef.status === "DRAFT" && data.is_owner && (
          <p className="mb-4 inline-flex items-center gap-2 rounded-full bg-gold/15 text-amber-800 px-4 py-1.5 text-sm font-bold" data-testid="draft-notice"><EyeOff size={14} /> Your profile is in Draft — only you can see it. Switch to Live in My Kitchen.</p>
        )}
        <div className="rounded-[2rem] bg-white border border-border shadow-soft p-6 sm:p-8 grid md:grid-cols-[auto_1fr_auto] gap-6 items-center">
          <img src={chef.avatar} alt={chef.display_name} className="w-36 h-36 sm:w-44 sm:h-44 rounded-[2rem] object-cover border border-border mx-auto md:mx-0" data-testid="chef-photo" />
          <div className="text-center md:text-left min-w-0">
            <h1 className="font-heading text-3xl sm:text-5xl font-black text-ink tracking-tight" data-testid="chef-name">{chef.display_name}</h1>
            <div className="mt-2 flex flex-wrap justify-center md:justify-start items-center gap-x-4 gap-y-1 text-ink-soft font-semibold">
              {chef.city && <span className="inline-flex items-center gap-1" data-testid="chef-city"><MapPin size={15} className="text-coral" /> {chef.city}</span>}
              <span className="inline-flex items-center gap-1"><Utensils size={15} className="text-coral" /> {chef.recipe_count} recipes</span>
              <span className="inline-flex items-center gap-1"><Users size={15} className="text-coral" /> {chef.followers_count} followers</span>
            </div>
            {chef.specialties?.length > 0 && (
              <div className="mt-3 flex flex-wrap justify-center md:justify-start gap-1.5" data-testid="chef-specialties">
                {chef.specialties.map((s) => <Link key={s} to={`/chefs?specialty=${encodeURIComponent(s)}`} className="rounded-full bg-secondary hover:bg-coral/10 hover:text-coral px-3 py-1 text-xs font-bold text-ink-soft transition-colors">{s}</Link>)}
              </div>
            )}
            {chef.bio && <p className="mt-3 text-ink-soft max-w-2xl">{chef.bio}</p>}
            <div className="mt-3 flex flex-wrap justify-center md:justify-start items-center gap-3">
              <span className="inline-flex items-center gap-2 rounded-full bg-gold/10 px-3 py-1.5" data-testid="chef-overall-rating">
                <RatingStars value={overall} size={15} /><span className="font-heading font-black text-ink">{overall}</span>
                <span className="text-xs text-ink-soft font-semibold">recipe rating · {Number(chef.rating_count || 0).toLocaleString()} ratings</span>
              </span>
              {chef.chef_rating_count > 0 && (
                <span className="inline-flex items-center gap-1.5 text-sm font-bold text-ink-soft" data-testid="chef-profile-rating"><Star size={14} className="fill-coral text-coral" /> Chef rating {chef.chef_rating_avg} ({chef.chef_rating_count})</span>
              )}
            </div>
          </div>
          <div className="flex flex-col items-center gap-3">
            {data.is_owner ? (
              <Link to="/kitchen" data-testid="edit-kitchen-link" className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-ink text-white font-bold hover:bg-ink-soft transition-colors"><Pencil size={16} /> My Kitchen</Link>
            ) : (
              <FollowButton chefId={chef.id} initialFollowing={data.is_following} initialCount={chef.followers_count} />
            )}
            <a href="#community" className="inline-flex items-center gap-1.5 text-sm font-bold text-ink-soft hover:text-coral min-h-[36px]"><MessageCircle size={15} /> Community</a>
          </div>
        </div>
      </div>

      {/* Recipe gallery */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-10" data-testid="recipe-gallery">
        <div className="flex items-baseline justify-between">
          <h2 className="font-heading text-2xl sm:text-3xl font-black text-ink">Recipes</h2>
          <p className="text-sm font-bold text-ink-soft">{data.recipes_total} published</p>
        </div>
        {recipes.length === 0 ? (
          <RecipeEmptyState title="No published recipes yet" subtitle="Follow this chef to be notified when they publish." />
        ) : (
          <>
            <div className="mt-5 grid gap-4 grid-cols-2 lg:grid-cols-3">
              {recipes.map((r) => <RecipeTile key={r.id} r={r} chefSlug={chef.slug} />)}
            </div>
            {hasMore && <div className="mt-8 text-center"><button onClick={loadMore} data-testid="recipes-view-all" className="h-12 px-7 rounded-full bg-white border border-border hover:border-coral font-bold text-ink transition-colors">View all recipes</button></div>}
          </>
        )}
      </section>

      {/* Community */}
      <section id="community" className="max-w-4xl mx-auto px-4 sm:px-6 mt-14 scroll-mt-24" data-testid="chef-community">
        <h2 className="font-heading text-2xl sm:text-3xl font-black text-ink">Community</h2>
        <p className="text-ink-soft mt-1">Talk to {chef.display_name}, share suggestions, and rate the chef.</p>
        <div className="mt-6">
          <CommentThread items={interactions} setItems={setInteractions} postUrl={`/chefs/${chef.id}/interactions`} testId="chef-thread" />
        </div>
        {!data.is_owner && (
          <div className="mt-10 grid lg:grid-cols-[1fr_1fr] gap-6 items-start">
            <RateBox testId="chef-rate" title="Rate this chef" subtitle="How would you rate their recipes overall?"
              postUrl={`/chefs/${chef.id}/rate`} initialValue={data.my_chef_rating?.value || 0} initialReview={data.my_chef_rating?.review}
              disabled={!data.can_rate} disabledReason="Open one of this chef's recipes first, then come back to rate them."
              onRated={(d) => { setReviews(d.reviews); setData((p) => ({ ...p, chef: { ...p.chef, chef_rating_avg: d.chef_rating_avg, chef_rating_count: d.chef_rating_count } })); }} />
            <div>
              <h3 className="font-heading text-xl font-black text-ink mb-3">Recent reviews</h3>
              <ReviewList reviews={reviews} emptyText="No chef reviews yet." />
            </div>
          </div>
        )}
        <div className="mt-10">
          <p className="text-sm font-bold text-ink-soft mb-2">Share this chef</p>
          <ShareButtons title={chef.display_name} path={`/chefs/${chef.slug}`} />
        </div>
      </section>
    </div>
  );
}
